// libraries
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useLocation } from 'react-router-dom'
import { cloneDeep, get, isEqual } from 'lodash'
import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { Stack } from '@mui/material'
import { Save as SaveIcon } from '@mui/icons-material'

// specific components
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import SettingsGrid from 'components/layouts/settings/SettingsGrid'
import SettingsGridWithBlade from 'components/layouts/settings/SettingsGridWithBlade'
import SettingsReportWithBlade from 'components/layouts/settings/SettingsReportWithBlade'
import SettingsColumnEdit from 'components/layouts/settings/SettingsColumnEdit'
import SettingsInput from 'components/layouts/settings/SettingsInput'
import SettingsDoubleInput from 'components/layouts/settings/SettingsDoubleInput'
import SettingsList from 'components/layouts/settings/SettingsList'
import SettingsScaleViewMaterials from 'components/layouts/settings/SettingsScaleViewMaterials'
import SettingsCurrencyConversion from 'components/layouts/settings/SettingsCurrencyConversion'
import SettingsCustomCheckTemplates from 'components/layouts/settings/SettingsCustomCheckTemplates'
import SettingsLockAccounting from 'components/layouts/settings/SettingsLockAccounting'
import SettingsUpload from 'components/layouts/settings/SettingsUpload'
import { SETTINGS_LAYOUTS } from 'components/containers/settings/utils/layouts'
import { defaultSettings } from 'components/containers/settings/utils/defaultSettings'
import AnalyticsContainer from 'components/containers/analytics'
import CustomerSupplierTypeMultiSelect from '../../layouts/settings/components/CustomerSupplierTypeMultiSelect'

// generic components
import AlertSnackbar from 'components/generic/snackbars'
import RouterPrompt from 'components/generic/modals/router-prompt/RouterPrompt'

// queries and mutations
import { useGetAccountsQuery, useUpdateAccountMutation } from 'services/accounts'
import {
  useCreateSupplierTypeMutation,
  useCreateVendorClassMutation,
  useGetSupplierTypesQuery,
  useGetVendorClassesQuery,
  usePatchSupplierTypeMutation,
  usePatchVendorClassMutation,
} from 'services/customers'
import { useCreateDriverMutation, useGetDriversQuery, useUpdateDriverMutation } from 'services/drivers'
import {
  useArchiveFeeOrPromotionPresetMutation,
  useCreateFeeOrPromotionPresetMutation,
  useGetFpSettingsQuery,
  useGetSettingsQuery,
  useUpdateFeeOrPromotionPresetMutation,
  useUpdateSettingsMutation,
} from 'services/settings'
import { useGetCurrencyConversionsQuery, useUpdateCurrencyConversionsMutation } from 'services/currencyConversions'
import {
  useCreatePaymentTermMutation,
  useGetPaymentTermsQuery,
  usePatchPaymentTermMutation,
} from 'services/paymentTerms'
import {
  useCreateAssetTypesMutation,
  useGetAssetTypesQuery,
  useUpdateAssetTypesMutation,
} from 'services/physicalAssets'
import { useGetLockAccountingPeriodQuery, useUpdateLockAccountingPeriodMutation } from 'services/lockAccounting'

// helpers & utils
import {
  ACCOUNT_PAYMENT_TYPE_ENUMS,
  ALL_PAYMENT_METHOD_OPTIONS,
  ASSET_CATEGORY,
  CHURN_ZERO_EVENTS as cz,
  THRESHOLD_UM_OPTIONS,
} from 'utils/constants'
import { trackEvent } from 'utils/tracking'
import { mergeSettings } from 'state/settings'
import { sortByName, sortSupplierTypeVendorClass } from 'utils/data-manipulation/common'
import { formOptions } from 'utils/validation/settings'
import { dispatchSnackbar } from 'utils/helpers'
import { getISOLocaleEndOfDay } from 'utils/data-manipulation/valueFormatter'
import { EXPLO_REPORTS } from '../analytics/reportsHelper'
import SettingsPaymentAuthorization from 'components/containers/settings/SettingsPaymentAuthorization'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import { FeeOrPromotionSettingResponseBody } from 'gen/rtk-openapi'

const SettingsContainer = ({ layoutType }) => {
  const {
    treatments,
    variables: { currencyExchangeRatesFF },
  } = useAppSelector(state => state.featureFlags || {})

  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const contentContext = useContext(ContentLayoutContext)
  const { yardId, organizationDetails } = useAppSelector(state => state.auth)
  const [apiRefDict, setApiRefDict] = useState({})
  const { defaultCurrency, defaultYardUm, cashDrawers, selectedCashDrawerId, hasAtm, details } = useAppSelector(
    state => state.settings,
  )

  const { gsPayEnabled } = details || {}

  // note: the argument "origin" is used so that this GET does not invalidate the redux settings (which could cause the page to rerender)
  const { data: settingsData, isFetching: isFetchingSettingsData } = useGetSettingsQuery(
    { origin: 'SettingsContainer' },
    { skip: !yardId, refetchOnMountOrArgChange: true },
  )

  const { data: accountsData, isLoading: isLoadingAccountsData } = useGetAccountsQuery(
    {},
    { skip: layoutType !== 'accounts' },
  )

  const { data: supplierTypesData, isLoading: isLoadingSupplierTypesData } = useGetSupplierTypesQuery(
    {},
    { skip: layoutType !== 'supplier-groupings' },
  )
  const { data: vendorClassesData, isLoading: isLoadingVendorClassesData } = useGetVendorClassesQuery(
    {},
    { skip: layoutType !== 'supplier-groupings' },
  )
  const { data: driversData, isLoading: isLoadingDriversData } = useGetDriversQuery(
    {},
    { skip: layoutType !== 'drivers' },
  )

  const { data: containerTypesData, isLoading: isLoadingContainerTypesData } = useGetAssetTypesQuery(
    { assetCategory: ASSET_CATEGORY.CONTAINER },
    { skip: layoutType !== 'asset-types' },
  )

  const { data: truckTypesData, isLoading: isLoadingTruckTypesData } = useGetAssetTypesQuery(
    { assetCategory: ASSET_CATEGORY.TRUCK },
    { skip: layoutType !== 'asset-types' },
  )

  const { data: trailerTypesData, isLoading: isLoadingTrailerTypesData } = useGetAssetTypesQuery(
    { assetCategory: ASSET_CATEGORY.TRAILER },
    { skip: layoutType !== 'asset-types' },
  )

  const { data: paymentTermsData, isLoading: isLoadingPaymentTermsData } = useGetPaymentTermsQuery(
    {},
    { skip: layoutType !== 'payment-terms' },
  )

  const { data: currencyConversionsData, isLoading: isLoadingCurrencyConversionsData } = useGetCurrencyConversionsQuery(
    {},
    { skip: layoutType !== 'overview' },
  )

  const { data: lockAccountingPeriodData, isLoading: isLoadingLockAccountingPeriodData } =
    useGetLockAccountingPeriodQuery({}, { skip: layoutType !== 'overview' })

  const [updateSetting, { isLoading: isLoadingUpdateSetting }] = useUpdateSettingsMutation()
  const [createSupplierType, { isLoading: isLoadingCreateSupplierType }] = useCreateSupplierTypeMutation()
  const [createVendorClass, { isLoading: isLoadingCreateVendorClass }] = useCreateVendorClassMutation()
  const [createDriver, { isLoading: isLoadingCreateDriver }] = useCreateDriverMutation()
  const [patchSupplierType, { isLoading: isLoadingPatchSupplierType }] = usePatchSupplierTypeMutation()
  const [patchVendorClass, { isLoading: isLoadingPatchVendorClass }] = usePatchVendorClassMutation()
  const [updateDriver, { isLoading: isLoadingUpdateDriver }] = useUpdateDriverMutation()
  const [updateCurrencyConversions, { isLoading: isLoadingUpdateCurrencyConversions }] =
    useUpdateCurrencyConversionsMutation()
  const [createPaymentTerm, { isLoading: isLoadingCreatePaymentTerm }] = useCreatePaymentTermMutation()
  const [patchPaymentTerm, { isLoading: isLoadingPatchPaymentTerm }] = usePatchPaymentTermMutation()
  const [createContainerType, { isLoading: isLoadingCreateContainerType }] = useCreateAssetTypesMutation()
  const [createTruckType, { isLoading: isLoadingCreateTruckType }] = useCreateAssetTypesMutation()
  const [createTrailerType, { isLoading: isLoadingCreateTrailerType }] = useCreateAssetTypesMutation()
  const [updateContainerType, { isLoading: isLoadingUpdateContainerType }] = useUpdateAssetTypesMutation()
  const [updateTruckType, { isLoading: isLoadingUpdateTruckType }] = useUpdateAssetTypesMutation()
  const [updateTrailerType, { isLoading: isLoadingUpdateTrailerType }] = useUpdateAssetTypesMutation()
  const [updateAccount, { isLoading: isLoadingUpdateAccount }] = useUpdateAccountMutation()
  const [updateLockAccountingPeriod, { isLoading: isLoadingUpdateLockAccountingPeriod }] =
    useUpdateLockAccountingPeriodMutation()

  const { data: feesAndPromotionsData, isLoading: isLoadingFeesAndPromotionsData } = useGetFpSettingsQuery({
    filter: JSON.stringify({ includeInactive: true }),
  })
  const [createFeeOrPromotionPreset, { isLoading: isLoadingCreateFeeOrPromotion }] =
    useCreateFeeOrPromotionPresetMutation()
  const [updateFeeOrPromotionPreset, { isLoading: isLoadingUpdateFeeOrPromotion }] =
    useUpdateFeeOrPromotionPresetMutation()
  const [archiveFeeOrPromotionPreset, { isLoading: isLoadingArchiveFeeOrPromotion }] =
    useArchiveFeeOrPromotionPresetMutation()

  const isLoading =
    isFetchingSettingsData ||
    isLoadingUpdateSetting ||
    isLoadingAccountsData ||
    isLoadingSupplierTypesData ||
    isLoadingVendorClassesData ||
    isLoadingDriversData ||
    isLoadingTruckTypesData ||
    isLoadingTrailerTypesData ||
    isLoadingCurrencyConversionsData ||
    isLoadingCreateSupplierType ||
    isLoadingCreateVendorClass ||
    isLoadingCreateDriver ||
    isLoadingPatchSupplierType ||
    isLoadingPatchVendorClass ||
    isLoadingUpdateDriver ||
    isLoadingUpdateCurrencyConversions ||
    isLoadingLockAccountingPeriodData ||
    isLoadingCreatePaymentTerm ||
    isLoadingPaymentTermsData ||
    isLoadingPatchPaymentTerm ||
    isLoadingContainerTypesData ||
    isLoadingCreateContainerType ||
    isLoadingUpdateContainerType ||
    isLoadingTruckTypesData ||
    isLoadingCreateTruckType ||
    isLoadingUpdateTruckType ||
    isLoadingTrailerTypesData ||
    isLoadingCreateTrailerType ||
    isLoadingUpdateTrailerType ||
    isLoadingUpdateAccount ||
    isLoadingUpdateLockAccountingPeriod ||
    isLoadingFeesAndPromotionsData ||
    isLoadingCreateFeeOrPromotion ||
    isLoadingUpdateFeeOrPromotion ||
    isLoadingArchiveFeeOrPromotion

  const methods = useForm({ ...formOptions, defaultValues: defaultSettings })
  const { reset, formState } = methods
  const { dirtyFields } = formState
  const isDirty = !!Object.keys(dirtyFields).length
  const [hasEditedGrid, setHasEditedGrid] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const location = useLocation()
  const lastHash = useRef('')

  // adds support for hash-based navigation, useful for directing a user to a specific section of the settings page
  useEffect(() => {
    if (location.hash) {
      lastHash.current = location.hash.slice(1) // safe hash for further use after navigation
    }
    if (lastHash.current) {
      setTimeout(() => {
        document.getElementById(lastHash.current)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        lastHash.current = ''
      }, 300)
    }
  }, [location])

  useEffect(() => {
    if (typeof settingsData === 'object') {
      const mergedSettings1 = mergeSettings(defaultSettings, settingsData)
      const mergedSettings2 = mergeSettings(mergedSettings1, {
        customerSettings: {
          supplierGroupings: {
            supplierTypes: cloneDeep(supplierTypesData?.Items || []).sort(sortSupplierTypeVendorClass),
            vendorClasses: cloneDeep(vendorClassesData?.Items || []).sort(sortSupplierTypeVendorClass),
          },
        },
      })
      const mergedSettings3 = mergeSettings(mergedSettings2, {
        overviewSettings: {
          overview: {
            lockAccountingPeriod: lockAccountingPeriodData,
          },
        },
      })
      const mergedSettings4 = mergeSettings(mergedSettings3, {
        dispatchSettings: {
          drivers: {
            drivers: cloneDeep(driversData?.Items || []).sort(sortByName),
          },
          assetTypes: {
            containerTypes: cloneDeep((containerTypesData as any)?.Items || []).sort(sortByName),
            truckTypes: cloneDeep((truckTypesData as any)?.Items || []).sort(sortByName),
            trailerTypes: cloneDeep((trailerTypesData as any)?.Items || []).sort(sortByName),
          },
        },
      })
      const mergedSettings5 = mergeSettings(mergedSettings4, {
        contractSettings: {
          paymentTerms: {
            listOfPaymentTerms: cloneDeep(paymentTermsData || []).sort((a, b) => {
              if (a.display.toLowerCase() < b.display.toLowerCase()) return -1
              if (a.display.toLowerCase() > b.display.toLowerCase()) return 1
              return 0
            }),
          },
        },
      })
      const mergedSettings6 = mergeSettings(mergedSettings5, {
        purchaseSettings: {
          feesAndPromotions: cloneDeep(feesAndPromotionsData?.Items || []).sort(sortByName),
        },
      })
      reset(mergedSettings6)
    }
  }, [
    settingsData,
    supplierTypesData,
    vendorClassesData,
    driversData,
    paymentTermsData,
    containerTypesData,
    truckTypesData,
    trailerTypesData,
    lockAccountingPeriodData,
    feesAndPromotionsData,
  ])

  const currentSettingsLayout = SETTINGS_LAYOUTS?.[layoutType] || {}

  const getFieldsForGrid = () => {
    const accounts = hasAtm
      ? accountsData?.Items
      : accountsData?.Items?.filter(account => account.type !== ACCOUNT_PAYMENT_TYPE_ENUMS.ATM)
    switch (layoutType) {
      case 'accounts':
        return accounts || []
      default:
        return null
    }
  }

  const getMetaBaseEmbeded = () => {
    switch (layoutType) {
      case 'cash-drawers': {
        const exploReport = EXPLO_REPORTS.find(report => report.id === 'Cash-Drawer-Report')
        return (
          <AnalyticsContainer reportId={exploReport.reportId} customParams={{ reportType: exploReport.reportType }} />
        )
      }
      default:
        return null
    }
  }

  const filteredPaymentMethods = useMemo(() => {
    return ALL_PAYMENT_METHOD_OPTIONS.filter(option => {
      if (option.id === 'gs_pay') return gsPayEnabled
      if (option.id === 'atm') return hasAtm
      return true
    })
  }, [gsPayEnabled, hasAtm])

  const renderSetting = data => {
    const settingData = cloneDeep(data)
    // This will set drawer options to the Select component with fetched values from backend
    if (layoutType === 'cash-drawers' && currentSettingsLayout?.subCategory === 'cashDrawers') {
      settingData.bladeComponents[0].defaultComponent.disabled = false
      settingData.bladeComponents[0].conditionalComponent.disabled = false
      settingData.bladeComponents[0].defaultComponent.defaultValue = selectedCashDrawerId
      settingData.bladeComponents[0].conditionalComponent.defaultValue = selectedCashDrawerId

      const cashDrawerOptions =
        cashDrawers?.map(option => ({
          id: option.id,
          name: option.accountName,
          type: option.type,
        })) || []
      settingData.bladeComponents[0].defaultComponent.items = cashDrawerOptions
      settingData.bladeComponents[0].conditionalComponent.items = cashDrawerOptions.filter(
        item => item.type !== ACCOUNT_PAYMENT_TYPE_ENUMS.ATM,
      )
    }

    // This will filter the payment method options in the dropdowns depending on whether the yard has different pay methods enabled/disabled
    if (layoutType === 'payment' && currentSettingsLayout?.subCategory === 'payment') {
      settingData?.listComponents?.forEach(component => {
        if (component.useFilteredPaymentMethods) {
          component.items = filteredPaymentMethods
        } else if (component.componentType === 'multiInputRow') {
          component.inputList.forEach(input => {
            if (input.useFilteredPaymentMethods) {
              input.dropdownOptions = filteredPaymentMethods
            }
          })
        }
      })
    }

    // This will update the subTitle and the dropdown options to display the defaultYardUm
    if (
      layoutType === 'inbound-tickets' &&
      currentSettingsLayout?.subCategory === 'inboundTickets' &&
      settingData.title === 'Weight Differential Threshold'
    ) {
      settingData.subTitle = settingData?.subTitle?.replace('{DEFAULT_YARD_UM}', `${defaultYardUm}`)
      settingData.listComponents = settingData.listComponents.map(listComponent => {
        if (listComponent.path !== 'purchaseSettings.inboundTickets.thresholdUM') return listComponent
        return {
          ...listComponent,
          items: THRESHOLD_UM_OPTIONS.map(option => {
            if (option.id !== 'pounds') return option
            return {
              ...option,
              name: defaultYardUm,
            }
          }),
        }
      })
    }

    switch (settingData.type) {
      case 'grid':
        return (
          <SettingsGrid
            {...settingData}
            setHasEditedGrid={setHasEditedGrid}
            apiRefDict={apiRefDict}
            setApiRefDict={setApiRefDict}
          />
        )
      case 'gridWithBlade':
        return (
          <SettingsGridWithBlade
            {...settingData}
            setHasEditedGrid={setHasEditedGrid}
            apiRefDict={apiRefDict}
            setApiRefDict={setApiRefDict}
            fields={getFieldsForGrid()}
          />
        )
      case 'reportWithBlade':
        return <SettingsReportWithBlade {...settingData} metaBaseEmbedComponent={getMetaBaseEmbeded()} />
      case 'columnEdit':
        return <SettingsColumnEdit {...settingData} />
      case 'input':
        return <SettingsInput {...settingData} />
      case 'doubleInput':
        return <SettingsDoubleInput {...settingData} />
      case 'list':
        return <SettingsList {...settingData} />
      case 'scaleViewMaterials':
        return (
          <SettingsScaleViewMaterials {...settingData} settingsConfiguration={get(settingsData, settingData.path)} />
        )
      case 'currencyConversion':
        return <SettingsCurrencyConversion {...settingData} />
      case 'customCheckTemplates':
        return <SettingsCustomCheckTemplates {...settingData} />
      case 'lockAccounting':
        return <SettingsLockAccounting />
      case 'upload':
        return <SettingsUpload {...settingData} />
      case 'paymentAuthorization':
        return (
          <SettingsPaymentAuthorization
            {...settingData}
            setHasEditedGrid={setHasEditedGrid}
            apiRefDict={apiRefDict}
            setApiRefDict={setApiRefDict}
          />
        )
      case 'customerSupplierTypeMultiSelect':
        return <CustomerSupplierTypeMultiSelect {...settingData} />
      default:
        return null
    }
  }

  const renderedSettings = useMemo(() => {
    if (!settingsData) return null
    if (!currentSettingsLayout?.settingsList) return null

    return currentSettingsLayout.settingsList.map(settingData => {
      // hide feature-flagged settings
      if (treatments[settingData?.treatment]?.treatment === 'off') {
        return null
      }
      return (
        <Stack
          padding={3}
          paddingBottom={settingData?.type === 'reportWithBlade' ? 0 : 3}
          key={`${layoutType}-${settingData.title}`}
        >
          {renderSetting(settingData)}
        </Stack>
      )
    })
  }, [
    settingsData,
    currentSettingsLayout,
    treatments,
    apiRefDict,
    accountsData,
    selectedCashDrawerId,
    cashDrawers,
    filteredPaymentMethods,
    defaultYardUm,
  ])

  const handleSuccess = (data, churnZeroDescription) => {
    reset(data)
    setHasEditedGrid(false)
    dispatchSnackbar(dispatch, 'success', 'Changes Saved')
    trackEvent({
      eventName: cz.ADJUST_SETTINGS,
      description: churnZeroDescription,
    })
  }

  const handleError = errorMsg => {
    trackEvent({
      eventName: cz.ADJUST_SETTINGS,
      errorMsg,
    })
    setErrorMessage(errorMsg)
  }

  const onSubmitSupplierGroupings = async data => {
    const { supplierTypes, vendorClasses } = data?.customerSettings?.supplierGroupings || {}
    const previousSupplierTypes = supplierTypesData?.Items || []
    const previousVendorClasses = vendorClassesData?.Items || []
    const promises: Promise<any>[] = []

    supplierTypes
      .filter(supplierType => !!supplierType?.name)
      .forEach(supplierType => {
        if (supplierType?.isNew) {
          promises.push(createSupplierType({ supplierType }))
        } else {
          const previousSupplierType = previousSupplierTypes.find(previous => previous?.id === supplierType?.id)
          if (
            previousSupplierType &&
            (previousSupplierType?.name !== supplierType?.name ||
              previousSupplierType?.active !== supplierType?.active ||
              previousSupplierType?.taxRate !== supplierType?.taxRate)
          ) {
            promises.push(
              patchSupplierType({
                body: supplierType,
                supplierTypeId: supplierType?.id,
              }),
            )
          }
        }
      })

    vendorClasses
      .filter(vendorClass => !!vendorClass?.name)
      .forEach(vendorClass => {
        if (vendorClass?.isNew) {
          promises.push(createVendorClass({ vendorClass }))
        } else {
          const previousVendorClass = previousVendorClasses.find(previous => previous.id === vendorClass?.id)
          if (
            previousVendorClass &&
            (previousVendorClass?.name !== vendorClass?.name || previousVendorClass?.active !== vendorClass?.active)
          ) {
            promises.push(
              patchVendorClass({
                body: vendorClass,
                vendorClassId: vendorClass?.id,
              }),
            )
          }
        }
      })

    const responses = await Promise.all(promises)
    const error = responses.find(response => !!response.error)?.error
    if (error) {
      handleError(error.data?.error || error.message || 'An error occurred while saving')
    } else {
      handleSuccess(data, 'Updated supplier types and vendor classes')
    }
  }

  const onSubmitDrivers = async data => {
    const { drivers } = data?.dispatchSettings?.drivers || {}
    const previousDrivers = driversData?.Items || []
    const promises: Promise<any>[] = []

    drivers
      .filter(driver => !!driver?.name)
      .forEach(driver => {
        if (driver?.isNew) {
          promises.push(
            createDriver({
              createDriverBody: {
                ...driver,
                organizationId: organizationDetails?.id || null,
              },
            }),
          )
        } else {
          const previousDriver = previousDrivers.find(previous => previous.id === driver?.id)
          if (
            previousDriver &&
            (previousDriver?.name !== driver?.name || previousDriver?.isActive !== driver?.isActive)
          ) {
            promises.push(updateDriver({ updateDriverBody: driver, driverId: driver?.id }))
          }
        }
      })

    const responses = await Promise.all(promises)
    const error = responses.find(response => !!response.error)?.error
    if (error) {
      handleError(error.data?.error || error.message || 'An error occurred while saving')
    } else {
      handleSuccess(data, 'Updated drivers')
    }
  }

  const onSubmitAssetTypes = async data => {
    const { containerTypes, truckTypes, trailerTypes } = data?.dispatchSettings?.assetTypes || {}

    const previousContainerTypes = (containerTypesData as any)?.Items || []
    const previousTruckTypes = (truckTypesData as any)?.Items || []
    const previousTrailerTypes = (trailerTypesData as any)?.Items || []
    const promises: Promise<any>[] = []

    containerTypes
      ?.filter(containerType => !!containerType?.name)
      .forEach(containerType => {
        if (containerType?.isNew) {
          promises.push(
            createContainerType({
              body: {
                ...containerType,
                organizationId: organizationDetails?.id || null,
              },
              assetCategory: ASSET_CATEGORY.CONTAINER,
            }),
          )
        } else {
          const previousContainerType = previousContainerTypes.find(previous => previous.id === containerType?.id)
          if (previousContainerType && previousContainerType?.name !== containerType?.name) {
            promises.push(
              updateContainerType({
                body: containerType,
                assetCategory: ASSET_CATEGORY.CONTAINER,
                assetTypeId: containerType?.id,
              }),
            )
          }
        }
      })
    truckTypes
      ?.filter(truckType => !!truckType?.name)
      .forEach(truckType => {
        if (truckType?.isNew) {
          promises.push(
            createTruckType({
              body: {
                ...truckType,
                organizationId: organizationDetails?.id || null,
              },
              assetCategory: ASSET_CATEGORY.TRUCK,
            }),
          )
        } else {
          const previousTruckType = previousTruckTypes.find(previous => previous.id === truckType?.id)
          if (previousTruckType && previousTruckType?.name !== truckType?.name) {
            promises.push(
              updateTruckType({
                body: truckType,
                assetCategory: ASSET_CATEGORY.TRUCK,
                assetTypeId: truckType?.id,
              }),
            )
          }
        }
      })
    trailerTypes
      ?.filter(trailerType => !!trailerType?.name)
      .forEach(trailerType => {
        if (trailerType?.isNew) {
          promises.push(
            createTrailerType({
              body: {
                ...trailerType,
                organizationId: organizationDetails?.id || null,
              },
              assetCategory: ASSET_CATEGORY.TRAILER,
            }),
          )
        } else {
          const previousTrailerType = previousTrailerTypes.find(previous => previous.id === trailerType?.id)
          if (previousTrailerType && previousTrailerType?.name !== trailerType?.name) {
            promises.push(
              updateTrailerType({
                body: trailerType,
                assetCategory: ASSET_CATEGORY.TRAILER,
                assetTypeId: trailerType?.id,
              }),
            )
          }
        }
      })

    const responses = await Promise.all(promises)
    const error = responses.find(response => !!response.error)?.error
    if (error) {
      handleError(error.data?.error || error.message || 'An error occurred while saving')
    } else {
      handleSuccess(data, ' asset types')
    }
  }

  const onSubmitOverview = async data => {
    try {
      if (currencyExchangeRatesFF && currencyConversionsData?.exchanges?.length > 0) {
        const formattedPayload = {
          exchanges: currencyConversionsData.exchanges.map(exchangeRate => ({
            fc: exchangeRate.fc,
            rate: data?.overviewSettings?.overview?.conversions?.[exchangeRate.id]?.conversion || 0,
            effectiveDate: new Date().toISOString(),
          })),
          homeCurrency: defaultCurrency,
        }

        const { error: updateCurrencyConversionsError } = await updateCurrencyConversions({ body: formattedPayload })
        if (updateCurrencyConversionsError) throw new Error(updateCurrencyConversionsError?.data?.error)
      }

      const formattedLockAccountingDate = getISOLocaleEndOfDay(
        (data.overviewSettings?.overview?.lockAccountingPeriod?.lockAccountingDate || new Date()).valueOf(),
      )

      const { error: updateLockAccountingPeriodError } = await updateLockAccountingPeriod({
        body: {
          lockAccountingDate: formattedLockAccountingDate,
          isLockedForAccounting: data.overviewSettings?.overview?.lockAccountingPeriod?.isLockedForAccounting,
        },
      })
      if (updateLockAccountingPeriodError) throw new Error(updateLockAccountingPeriodError?.data?.error)

      handleSuccess(data, 'Updated overview')
    } catch (error) {
      handleError((error as any)?.data?.error || (error as any)?.message || 'An error occurred while saving')
    }
  }

  const onSubmitPaymentTerms = async data => {
    const { listOfPaymentTerms } = data?.contractSettings?.paymentTerms || []
    const previousPaymentTerms = paymentTermsData || []
    const promises: Promise<any>[] = []

    const editablePaymentTerms = listOfPaymentTerms.filter(paymentTerm => !paymentTerm.nonEditable)
    const missingFields = editablePaymentTerms.some(paymentTerm => !paymentTerm.display || !paymentTerm.days)
    if (missingFields) {
      handleError('You must specify a display name and number of days in all payment terms.')
      return
    }

    listOfPaymentTerms
      .filter(paymentTerm => !!paymentTerm.display)
      .forEach(paymentTerm => {
        if (paymentTerm.isNew) {
          promises.push(
            createPaymentTerm({
              paymentTerm: {
                display: paymentTerm.display,
                days: paymentTerm.days,
                active: paymentTerm.active,
              },
            }),
          )
        } else {
          const previousPaymentTerm = previousPaymentTerms.find(previous => previous.id === paymentTerm.id)
          if (
            previousPaymentTerm &&
            (previousPaymentTerm?.display !== paymentTerm.display ||
              previousPaymentTerm?.days !== paymentTerm.days ||
              previousPaymentTerm?.active !== paymentTerm.active)
          ) {
            promises.push(
              patchPaymentTerm({
                body: paymentTerm,
                paymentTermId: paymentTerm.id,
              }),
            )
          }
        }
      })
    const responses = await Promise.all(promises)
    const error = responses.find(response => !!response.error)?.error
    if (error) {
      handleError(error.data?.error || error.message || 'An error occurred while saving')
    } else {
      handleSuccess(data, 'Updated payment terms')
    }
  }

  const onSubmitAccounts = async data => {
    const { accounts } = data?.purchaseSettings?.accounts || {}
    const accountsDict = {}
    accountsData?.Items?.forEach(account => {
      accountsDict[account.id] = account
    })
    const editedAccounts = accounts.filter(account => {
      const accountNameIsChanged = account.accountName !== accountsDict[account.id].accountName
      const ledgerCodeIsChanged = account.ledgerCode !== accountsDict[account.id].ledgerCode
      return accountNameIsChanged || ledgerCodeIsChanged
    })
    if (editedAccounts.length === 0) {
      handleSuccess(data, 'Updated accounts')
      return
    }
    if (editedAccounts.some(account => !account.accountName)) {
      dispatchSnackbar(dispatch, 'error', 'Some accounts are missing an account name')
      return
    }
    const promises = editedAccounts.map(account =>
      updateAccount({
        body: {
          id: account.id,
          accountName: account.accountName,
          type: account.type,
          ledgerCode: account.ledgerCode,
          currency: account.currency,
        },
        accountId: account.id,
      }),
    )

    const responses = await Promise.all(promises)
    const error = responses.find(response => !!response.error)?.error
    if (error) {
      handleError(error.data?.error || error.message || 'An error occurred while saving')
    } else {
      handleSuccess(data, 'Updated accounts')
    }
  }

  const onSubmitAdjustments = async data => {
    const fpFormState = data?.purchaseSettings?.feesAndPromotions
    const deductionFormState = data?.purchaseSettings?.deductions
    const deductionsEdited = !!dirtyFields?.purchaseSettings?.deductions
    const feesAndPromotionsEdited = !!dirtyFields?.purchaseSettings?.feesAndPromotions

    const newFPs: FeeOrPromotionSettingResponseBody[] = []
    const editedFPs: FeeOrPromotionSettingResponseBody[] = []
    const archivedFPs: FeeOrPromotionSettingResponseBody[] = []
    let promises: Promise<any>[] = []

    if (deductionsEdited) {
      const deductionsUpdateCmd = updateSetting({
        category: 'purchaseSettings',
        subCategory: 'deductions',
        body: deductionFormState,
      }).unwrap()
      promises.push(deductionsUpdateCmd)
    }

    if (feesAndPromotionsEdited) {
      fpFormState?.forEach(fp => {
        const original = feesAndPromotionsData?.Items.find(og => og.id === fp.id)
        if (original && !isEqual(original, fp)) {
          editedFPs.push(fp)
        } else if (!original) {
          newFPs.push(fp)
        }
      })
      // check for fees and promotions that were archived
      feesAndPromotionsData?.Items.forEach(original => {
        if (!fpFormState?.find(fp => fp.id === original.id)) {
          archivedFPs.push(original)
        }
      })
    }

    // Manual validation for new F&P rows
    const requiredFields = [
      { key: 'name', label: 'Name' },
      { key: 'eligiblePaymentTypes', label: 'Payment Types' },
      { key: 'type', label: 'Fee / Promotion Specification' },
      { key: 'amount', label: 'Amount' },
      { key: 'amountType', label: 'UM' },
    ]
    for (const fp of newFPs) {
      const missing = requiredFields.find(({ key }) => {
        const v = fp[key]
        if (Array.isArray(v)) return v.length === 0
        return v == null || v === ''
      })
      if (missing) {
        handleError(`Missing ${missing.label} in new fee or promotion.`)
        return
      }
    }

    const fpCreatePromises = newFPs.map(fp => {
      return createFeeOrPromotionPreset({
        feeOrPromotionSettingBody: fp,
      }).unwrap()
    })
    promises = promises.concat(fpCreatePromises)
    const fpUpdatePromises = editedFPs.map(fp => {
      return updateFeeOrPromotionPreset({
        updateFeeOrPromotionSettingBody: fp,
        feeOrPromotionId: fp.id,
      }).unwrap()
    })
    promises = promises.concat(fpUpdatePromises)
    const fpArchivePromises = archivedFPs.map(fp => {
      return archiveFeeOrPromotionPreset({
        feeOrPromotionId: fp.id,
      }).unwrap()
    })
    promises = promises.concat(fpArchivePromises)

    try {
      await Promise.all(promises)
      handleSuccess(data, 'Updated adjustments')
    } catch (error) {
      console.error(error)
      handleError('An error occurred while saving adjustments')
    }
  }

  const onSubmitSettings = async data => {
    const basePathLength = '/app/settings/'.length
    const updatedSettings = pathname.slice(basePathLength).replace('/', ': ')

    try {
      const { category, subCategory } = currentSettingsLayout
      await updateSetting({
        category,
        subCategory,
        body: data?.[category]?.[subCategory],
      })
      handleSuccess(data, updatedSettings)
    } catch (error) {
      handleError((error as any)?.data?.error || (error as any)?.message || 'An error occurred while saving')
    }
  }

  const onSubmit = async data => {
    if (layoutType === 'supplier-groupings') {
      await onSubmitSupplierGroupings(data)
    } else if (layoutType === 'drivers') {
      await onSubmitDrivers(data)
    } else if (layoutType === 'asset-types') {
      await onSubmitAssetTypes(data)
    } else if (layoutType === 'overview') {
      await onSubmitOverview(data)
    } else if (layoutType === 'payment-terms') {
      await onSubmitPaymentTerms(data)
    } else if (layoutType === 'accounts') {
      await onSubmitAccounts(data)
    } else if (layoutType === 'adjustments') {
      await onSubmitAdjustments(data)
    } else {
      await onSubmitSettings(data)
    }
  }

  useEffect(() => {
    const canSave = !isLoading && (isDirty || hasEditedGrid)
    const actions = [
      {
        text: canSave ? 'Save Changes' : 'No Changes to Save',
        variant: canSave ? 'contained' : 'outlined',
        icon: canSave ? <SaveIcon /> : null,
        disabled: !canSave,
        id: 'save-button',
        onClick: methods.handleSubmit(onSubmit),
      },
    ]
    contentContext.setContainerActions(actions)
  }, [isDirty, hasEditedGrid, isLoading])

  return (
    <>
      <AlertSnackbar
        message={errorMessage}
        isOpen={!!errorMessage}
        severity="error"
        handleClose={() => setErrorMessage('')}
      />
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <RouterPrompt
            title="Cancel Changes"
            question="Are you sure you want to cancel your changes?"
            description="All unsaved data will be lost."
            when={isDirty || hasEditedGrid}
          />
          {renderedSettings}
        </form>
      </FormProvider>
    </>
  )
}

SettingsContainer.propTypes = {
  layoutType: PropTypes.string,
}

SettingsContainer.defaultProps = {
  layoutType: '',
}

export default SettingsContainer
